import React, { useState, useEffect, memo } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { userSignUp, clearAuthErrors } from 'actions/auth';
import { signupErrorMessageSelector } from 'selectors/auth';
import { IAuthData, IError } from 'types';
import { logEvent } from 'services/amplitude';
import RadiusInput from 'components/common/formElements/radiusInput';
import Form from 'components/common/formElements/form';
import PswPopOver from 'components/common/helpers/passwordHelper';
import Logo from 'components/common/logo';
import Robot from 'style/imgs/Backpack.svg';

import buttons from 'style/buttons.module.scss';
import styles from './index.module.scss';

type QueryParams = {
  referralId: string;
};

const SignUp: React.FC = () => {
  const errorMessage = (useSelector(
    signupErrorMessageSelector
  ) as unknown) as IError;
  const dispatch = useDispatch();
  const { referralId } = useParams<QueryParams>();
  const [password, setPassword] = useState<string>('');

  const schema = yup.object().shape({
    email: yup.string().email().required(),
    password: yup
      .string()
      .min(8)
      .max(64)
      .matches(/^(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)[a-zA-Z\d]{8,64}$/)
      .required('Password is required'),
    passwordConfirm: yup
      .string()
      .oneOf([yup.ref('password'), null])
      .required(),
  });

  const { register, handleSubmit, errors } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = (values: IAuthData) => {
    let requestData;
    if (referralId) {
      requestData = {
        email: values.email?.toLocaleLowerCase(),
        password: values.password,
        userType: localStorage.getItem('role') || 'parent',
        referralOwnerId: +referralId || 0,
      };
    } else {
      requestData = {
        email: values.email,
        password: values.password,
        userType: localStorage.getItem('role') || '',
      };
    }
    dispatch(userSignUp(requestData));
    logEvent('SignUp');
  };

  const handleChangePassword = (e: React.FormEvent<HTMLInputElement>) => {
    setPassword(e.currentTarget.value);
  };

  useEffect(() => {
    dispatch(clearAuthErrors('signUp'));
  }, [dispatch]);

  return (
    <div className={styles.signUp}>
      <div className={styles.signUpLeft}>
        <div className={styles.robot}>
          <img src={Robot} alt="img" />
        </div>
      </div>
      <div className={styles.signUpRight}>
        <div className={styles.logo}>
          <Logo />
        </div>
        <h1 className="deep-gray-heading">Sign Up</h1>
        <div className={styles.trapezoid}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <RadiusInput
              variant="outlined"
              type="text"
              label="Enter your email"
              name="email"
              ref={register}
              error={!!errors.email}
            />
            {!!errorMessage && (
              <span className="error-message">{errorMessage}</span>
            )}
            <RadiusInput
              variant="outlined"
              id="password"
              type="password"
              label="Create your password"
              name="password"
              onChange={handleChangePassword}
              ref={register}
              error={!!errors.password}
            />
            <PswPopOver password={password} target="password" />
            <RadiusInput
              variant="outlined"
              type="password"
              label="Confirm your password"
              name="passwordConfirm"
              ref={register}
              error={!!errors.passwordConfirm}
            />
            {/* {!!errors.passwordConfirm && (
              <span className="error-message">Passwords must match</span>
            )} */}
            <button type="submit" className={buttons.buttomPrimary}>
              Sign Up
            </button>
          </Form>
        </div>
        <div className={`gray-text ${styles.signUpLink}`}>
          Already have an account?{' '}
          <Link className="orange-heading" to="/login/">
            Log In now
          </Link>
        </div>
        <a
          href="https://anetaed.zendesk.com/hc/en-us"
          target="_blank"
          rel="noreferrer"
          className="question"
        >
          ?
        </a>
      </div>
    </div>
  );
};

export default memo(SignUp);
